import { useQuery } from 'react-query';
import {
	getAllHiringAssessments,
	getHiringAssessment,
	getHiringAssessmentMeta,
	getHiringAssessmentSkills,
	guestUserValidateJwt,
	hiringAssessmentDashboard,
} from 'src/apis/hiringAssessment';
import {
	GetHiringAssessmentDashboardResponse,
	GetHiringAssessmentListResponse,
	GetHiringAssessmentMetaResponse,
	GetHiringAssessmentResponse,
	GetHiringAssessmentSkillResponse,
	GuestUserValidateJwtAPIResponse,
	UserPreferenceType,
} from 'types';

export const useGetAllHiringAssessments = (
	hiringAssessmentId?: string,
	onComplete?: (data: GetHiringAssessmentListResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getAllHiringAssessments', hiringAssessmentId],
		queryFn: async () => await getAllHiringAssessments({ id: hiringAssessmentId }),
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGuestUserValidateJwt = (
	token: string,
	onComplete?: (data: GuestUserValidateJwtAPIResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['guestUserValidateJwt', token],
		queryFn: async () => await guestUserValidateJwt({ token }),
		staleTime: 0,
		refetchOnMount: true,
		enabled: !!token,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useHiringAssessmentDashboard = (
	onComplete?: (data: GetHiringAssessmentDashboardResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['hiringAssessmentDashboard'],
		queryFn: async () => await hiringAssessmentDashboard(),
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetHiringAssessment = (
	data: {
		hiringAssessmentId: string;
		token: string;
	},
	onComplete?: (data: GetHiringAssessmentResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getHiringAssessment', data.hiringAssessmentId, data.token],
		queryFn: async () => await getHiringAssessment(data),
		staleTime: 0,
		refetchOnMount: true,
		enabled: !!data.hiringAssessmentId,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetHiringAssessmentMeta = (
	data: {
		hiringAssessmentId: string;
	},
	onComplete?: (data: GetHiringAssessmentMetaResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getHiringAssessmentMeta', data.hiringAssessmentId],
		queryFn: async () => await getHiringAssessmentMeta(data),
		staleTime: 0,
		refetchOnMount: true,
		enabled: !!data.hiringAssessmentId,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetHiringAssessmentSkills = (
	data: {
		userPreferenceSnapshot: UserPreferenceType;
	},
	onComplete?: (data: GetHiringAssessmentSkillResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getHiringAssessmentSkills'],
		queryFn: async () => await getHiringAssessmentSkills(data),
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};
